"use client";

import type { Prompt } from "@/lib/types";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useStore } from "@/lib/store";
import Link from "next/link";
import { ShoppingCart, Copy, Check } from "lucide-react";
import { useState } from "react";

interface PromptCardProps {
  prompt: Prompt;
  onAddToCart?: (prompt: Prompt) => void; // 선택적 prop으로 유지 (하위 호환성)
}

export function PromptCard({ prompt }: PromptCardProps) {
  const { addToCart, isInCart, isLoggedIn, hasPurchased } = useStore();
  const { toast } = useToast();
  const inCart = isInCart(prompt.id);
  const purchased = hasPurchased(prompt.id);
  const [copied, setCopied] = useState(false);

  const handleAddToCart = () => {
    if (!isLoggedIn) {
      toast({
        title: "로그인이 필요합니다",
        description: "장바구니를 사용하려면 로그인해주세요.",
        variant: "destructive",
      });
      return;
    }

    addToCart({ promptId: prompt.id, prompt });
    toast({
      title: "장바구니에 담았습니다",
      description: `${prompt.title}이(가) 장바구니에 추가되었습니다.`,
    });
  };

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt.prompt_text);
      setCopied(true);
      toast({
        title: "프롬프트가 복사되었습니다",
        description: "클립보드에 프롬프트가 복사되었습니다.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "복사 실패",
        description: "프롬프트를 복사하는데 실패했습니다.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow flex flex-col">
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={prompt.thumbnail || "/placeholder.svg"}
          alt={prompt.title}
          className="w-full h-full object-cover"
        />
      </div>

      <CardContent className="p-4 flex-1 flex flex-col">
        <h3 className="font-semibold text-lg leading-snug mb-2 line-clamp-2">
          {prompt.title}
        </h3>

        <div className="text-xl font-bold text-primary mb-4">
          {prompt.price.toLocaleString()}원
        </div>

        <div className="mt-auto flex gap-2">
          <Link href={`/prompts/${prompt.id}`} className="flex-1">
            <Button variant="outline" className="w-full bg-transparent">
              상세보기
            </Button>
          </Link>
          {purchased ? (
            <Button
              onClick={handleCopyPrompt}
              className="flex-1 bg-green-500 hover:bg-green-600 text-white"
              variant="default"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  복사됨
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1" />
                  프롬프트 복사
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleAddToCart}
              disabled={inCart}
              className="flex-1"
              variant={inCart ? "secondary" : "default"}
            >
              {inCart ? (
                "장바구니에 있음"
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4 mr-1" />
                  장바구니 담기
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
